import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { ApplicationConfigService } from 'services/application-config.service';
import { createRequestOption } from 'app/shared/util/request-util';
import { GlobalInsurance, InsuranceItem } from '../models/insurance-item.model';
import { ApiProviderService } from './api-provider.service';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';
import { IBaryTableFetchService, TableOptions } from './i-bary-table-fetch-service';
import { mapAsBody } from '../app/shared/util/request-util';
import { map } from 'rxjs/operators';

export type EntityResponseType = HttpResponse<InsuranceItem>;
export type EntityArrayResponseType = HttpResponse<InsuranceItem[]>;

@Injectable({ providedIn: 'root' })
export class InsuranceItemService implements IBaryTableFetchService<InsuranceItem> {
  protected resourceUrl = API_ENDPOINTS.INSURANCE_ITEMS;
  protected insuranceItemsIdUrl = API_ENDPOINTS.INSURANCE_ITEMS_ID;
  protected insuranceItemsByOfferIdUrl = API_ENDPOINTS.INSURANCE_ITEMS_OFFER_ID;
  protected insuranceItemsGlobal = API_ENDPOINTS.INSURANCE_ITEMS_GLOBAL;

  constructor(protected http: HttpClient, protected apiProvider: ApiProviderService, protected applicationConfigService: ApplicationConfigService) {}

  fillDataToShow(entities: InsuranceItem[]): Observable<InsuranceItem[]> {
    return of(entities);
  }

  find(id: number): Observable<InsuranceItem> {
    return this.apiProvider.get<InsuranceItem>(this.insuranceItemsIdUrl(id)).pipe(
      mapAsBody,
      map((val: any) => new InsuranceItem(val))
    );
  }

  findAllByOfferId(id: number): Observable<EntityArrayResponseType> {
    const params = new HttpParams().append('offerId.equals', id);
    return this.apiProvider.publicGet<InsuranceItem[]>(`${this.insuranceItemsByOfferIdUrl(id)}`, {
      params,
      observe: 'response',
    });
  }

  query(req?: any, tableOption?: TableOptions): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.apiProvider
      .publicGet<InsuranceItem[]>(this.insuranceItemsByOfferIdUrl(tableOption?.identifier), {
        params: options,
        observe: 'response',
      })
      .pipe(
        map((res: EntityArrayResponseType) => {
          return new HttpResponse<InsuranceItem[]>({
            body: (res.body || []).map(v => new InsuranceItem(v)),
            status: res.status,
            headers: res.headers,
            url: res.url as string,
          });
        })
      );
  }

  updateOrder(insuranceItems: InsuranceItem[], tableOption?: TableOptions): Observable<InsuranceItem[]> {
    return this.apiProvider.put<InsuranceItem[]>(this.insuranceItemsByOfferIdUrl(tableOption?.identifier), insuranceItems).pipe(mapAsBody);
  }

  updateGlobal(globalInsurance: GlobalInsurance): Observable<GlobalInsurance> {
    return this.apiProvider.put<GlobalInsurance>(this.insuranceItemsGlobal(), globalInsurance).pipe(mapAsBody);
  }

  getGlobal(): Observable<GlobalInsurance> {
    return this.apiProvider.get<GlobalInsurance>(this.insuranceItemsGlobal()).pipe(mapAsBody);
  }

  create(insuranceItem: InsuranceItem, tableOption?: TableOptions): Observable<EntityResponseType> {
    return this.apiProvider.post<InsuranceItem>(this.insuranceItemsByOfferIdUrl(tableOption?.identifier), insuranceItem);
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.apiProvider.delete(`${this.resourceUrl}/${id}`);
  }

  update(insuranceItem: InsuranceItem): Observable<InsuranceItem> {
    return this.apiProvider.put<InsuranceItem>(`${this.resourceUrl}/${insuranceItem.id}`, insuranceItem).pipe(mapAsBody);
  }
}
